import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: "" });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }

    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", ...data });
          toast.success("Pago exitoso");
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        if (attempts.current >= 8) {
          setState({ status: "timeout" });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts.current < 5) setTimeout(poll, 2000);
        else setState({ status: "error" });
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="max-w-md mx-auto fade-up" data-testid="payment-success-page">
      <Card>
        <CardContent className="p-10 text-center space-y-4">
          {state.status === "polling" && (
            <>
              <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin" />
              <h1 className="font-display text-2xl">Verificando pago...</h1>
              <p className="text-sm text-muted-foreground">Estamos confirmando tu pago con Stripe.</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <CheckCircle2 className="w-14 h-14 mx-auto text-[#4A6E4F]" />
              <h1 className="font-display text-2xl">¡Pago confirmado!</h1>
              <p className="text-sm text-muted-foreground">
                Tu consulta de {(state.amount_total || 2000) / 100}€ ha sido pagada correctamente.
              </p>
              <Button className="bg-primary hover:bg-primary/90 w-full" onClick={() => nav("/app/appointments")} data-testid="back-to-appts">
                Ver mis citas
              </Button>
            </>
          )}
          {(state.status === "expired" || state.status === "error" || state.status === "timeout") && (
            <>
              <XCircle className="w-14 h-14 mx-auto text-destructive" />
              <h1 className="font-display text-2xl">Pago no confirmado</h1>
              <p className="text-sm text-muted-foreground">
                {state.status === "expired" ? "La sesión de pago expiró." :
                  state.status === "timeout" ? "No pudimos verificar el pago a tiempo. Recibirás confirmación por email." :
                  "Hubo un problema verificando el pago."}
              </p>
              <Button variant="outline" className="w-full" onClick={() => nav("/app/appointments")} data-testid="back-to-appts">
                Volver a mis citas
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
